import type {
  GetInsightCardsResponse,
  InsightCard,
} from "@/api/ai-chat-insight-cards";

import { cloneFixture } from "@/mocks/test-fixture-utils";

export const AI_CHAT_ENDPOINTS = {
  GET_INSIGHT_CARDS: "ai_chat_get_insight_cards",
} as const;

// Sunday that ends the most recent Mon–Sun week, for a realistic weekRange.
const AS_OF_DATE = "2026-11-01";

const insightCards: InsightCard[] = [
  {
    insight_id: "mock-ins-1",
    card_key: "mock-ck-1",
    content_hash: "mock-h1",
    feed_rank: 1,
    insight_domain: "conversions",
    insight_title:
      "Your homepage got 14,272 sessions last week but converted at 2.59%, down from 3.4%.",
    insight_body:
      "Conversion rate on the homepage dropped 24% week over week while traffic held steady, so the drop is about on-page behavior rather than acquisition.",
    why_it_matters:
      "The homepage is your highest-traffic entry point, so small conversion swings move total signups a lot.",
    suggested_prompt: "Why did my homepage conversion rate drop last week?",
    is_new: true,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Traffic and conversions",
    hover_question: "What changed on my homepage this week?",
    recommended_action: "Review the homepage's primary call to action",
    severity: 3,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-2",
    card_key: "mock-ck-2",
    content_hash: "mock-h2",
    feed_rank: 2,
    insight_domain: "attribution",
    insight_title:
      "Paid search is credited with 38% of conversions under last-touch but 21% under linear.",
    insight_body:
      "The gap suggests paid search often closes conversions that other channels opened. Comparing models can reveal under-credited assist channels.",
    why_it_matters:
      "Budget decisions based on last-touch alone can over-invest in closing channels and starve the ones driving discovery.",
    suggested_prompt:
      "How does my channel mix change across attribution models?",
    is_new: false,
    is_changed: true,
    as_of_date: AS_OF_DATE,
    insight_badge: "Traffic and conversions",
    hover_question: "Which channels are under-credited by last-touch?",
    recommended_action: "",
    severity: 1,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-3",
    card_key: "mock-ck-3",
    content_hash: "mock-h3",
    feed_rank: 3,
    insight_domain: "wtm",
    insight_title:
      "3 new third-party trackers started firing on your checkout pages.",
    insight_body:
      "These trackers were not present the prior week. Unreviewed trackers on checkout pages can collect data without a consent basis.",
    why_it_matters:
      "New trackers on pages that handle sensitive data are a common source of compliance risk.",
    suggested_prompt: "What new trackers appeared on my site last week?",
    is_new: true,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Web trackers",
    hover_question: "Which new trackers should I review?",
    recommended_action: "Review and classify the new trackers",
    severity: 2,
    is_resolved: false,
    resolved_on: "",
    trackers: [
      {
        tracker_vendor: "DoubleClick",
        risk_level: "high",
        first_detected: "2026-10-28",
      },
      {
        tracker_vendor: "Hotjar",
        risk_level: "medium",
        first_detected: "2026-10-29",
      },
      {
        tracker_vendor: "Segment",
        risk_level: "low",
        first_detected: "2026-10-30",
      },
    ],
  },
  {
    insight_id: "mock-ins-4",
    card_key: "mock-ck-4",
    content_hash: "mock-h4",
    feed_rank: 4,
    insight_domain: "events_health",
    insight_title:
      "You sent 12% of the events you collected last week to at least one destination.",
    insight_body:
      "88% of collected events were not forwarded to any destination. This can mean missing event mappings or a destination that was paused.",
    why_it_matters:
      "Events that are collected but never delivered are invisible in your downstream analytics and ad platforms.",
    suggested_prompt: "Which events are being collected but not delivered?",
    is_new: false,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Tracking",
    hover_question: "Why are so few events reaching my destinations?",
    recommended_action: "",
    severity: 1,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-5",
    card_key: "mock-ck-5",
    content_hash: "mock-h5",
    feed_rank: 5,
    insight_domain: "heatmap",
    insight_title:
      "Rage clicks on your pricing page jumped 3.1x on the 'Compare plans' toggle.",
    insight_body:
      "Visitors clicked the toggle repeatedly without the comparison expanding, which points to a broken or slow interaction on the pricing page.",
    why_it_matters:
      "Rage clicks on a pricing page signal friction at the moment a visitor is closest to buying.",
    suggested_prompt: "Where are visitors rage clicking on my pricing page?",
    is_new: true,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Heatmaps",
    hover_question: "What interaction is frustrating my pricing visitors?",
    recommended_action: "Inspect the 'Compare plans' toggle behavior",
    severity: 3,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-6",
    card_key: "mock-ck-6",
    content_hash: "mock-h6",
    feed_rank: 6,
    insight_domain: "consent",
    insight_title:
      "9.4% of EU sessions fired tracking before a consent decision was recorded.",
    insight_body:
      "Tags loaded ahead of the consent banner response for a slice of EU traffic, so data left the page without a consent basis.",
    why_it_matters:
      "Pre-consent tracking in the EU is a direct GDPR exposure and undermines your consent records.",
    suggested_prompt: "Which tags fire before consent for my EU visitors?",
    is_new: true,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Consent",
    hover_question: "Am I tracking EU visitors before consent?",
    recommended_action: "Gate early-firing tags behind consent",
    severity: 3,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-7",
    card_key: "mock-ck-7",
    content_hash: "mock-h7",
    feed_rank: 7,
    insight_domain: "visitors",
    insight_title:
      "Returning visitors converted at 5.8% last week versus 1.9% for new visitors.",
    insight_body:
      "Returning visitors convert 3x better, but they made up only 22% of sessions. Re-engagement has outsized leverage right now.",
    why_it_matters:
      "A small lift in return visits compounds into meaningful conversion gains given the 3x rate gap.",
    suggested_prompt: "How do returning and new visitors convert differently?",
    is_new: false,
    is_changed: true,
    as_of_date: AS_OF_DATE,
    insight_badge: "Traffic and conversions",
    hover_question: "Should I invest more in re-engaging visitors?",
    recommended_action: "",
    severity: 1,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-8",
    card_key: "mock-ck-8",
    content_hash: "mock-h8",
    feed_rank: 8,
    insight_domain: "page_performance",
    insight_title:
      "Median load time on your top landing page rose from 1.4s to 2.8s.",
    insight_body:
      "Load time doubled week over week and correlated with a 0.6pt drop in that page's conversion rate.",
    why_it_matters:
      "Slower pages lose impatient visitors before they ever see your offer.",
    suggested_prompt: "Which pages slowed down and hurt conversions?",
    is_new: true,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Traffic and conversions",
    hover_question: "Did a slow page cost me conversions this week?",
    recommended_action: "Audit the landing page's largest assets",
    severity: 2,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-9",
    card_key: "mock-ck-9",
    content_hash: "mock-h9",
    feed_rank: 9,
    insight_domain: "events_health",
    insight_title:
      "Your 'Signup Completed' event stopped firing on Safari two days ago.",
    insight_body:
      "The event has zero Safari volume since Thursday while other browsers are unchanged, which points to a browser-specific tracking break.",
    why_it_matters:
      "A silent event break hides real conversions and skews every downstream report that depends on it.",
    suggested_prompt: "Why did 'Signup Completed' stop firing on Safari?",
    is_new: true,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Tracking",
    hover_question: "Which events recently broke on a single browser?",
    recommended_action: "Debug the 'Signup Completed' trigger on Safari",
    severity: 3,
    is_resolved: false,
    resolved_on: "",
  },
  {
    insight_id: "mock-ins-10",
    card_key: "mock-ck-10",
    content_hash: "mock-h10",
    feed_rank: 10,
    insight_domain: "wtm",
    insight_title:
      "A marketing tag is sending email addresses in a URL parameter to a third party.",
    insight_body:
      "Outbound requests from one tag include a plaintext email in the query string, exposing PII to a vendor without hashing.",
    why_it_matters:
      "Leaking identifiable data to third parties is both a privacy risk and a likely policy violation.",
    suggested_prompt: "Which tags are leaking PII to third parties?",
    is_new: false,
    is_changed: false,
    as_of_date: AS_OF_DATE,
    insight_badge: "Web trackers",
    hover_question: "Is any tag exposing customer emails?",
    recommended_action: "Strip the email parameter from the tag's requests",
    severity: 3,
    is_resolved: false,
    resolved_on: "",
    trackers: [
      {
        tracker_vendor: "Taboola",
        risk_level: "high",
        first_detected: "2026-10-27",
      },
    ],
  },
];

export function getInsightCardsResponse(): GetInsightCardsResponse {
  return cloneFixture({ cards: insightCards });
}
